import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Plus, Minus, X, Check, Loader2, Package, Sparkles, ShieldCheck } from 'lucide-react';
import { publicApi } from '../lib/api';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CheckoutModal from '../components/CheckoutModal';

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const ProductCard = ({ product, onAdd }) => {
  const [added, setAdded] = useState(false);
  const isComingSoon = product.coming_soon;

  const handleAdd = () => {
    if (isComingSoon) return;
    onAdd(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <div className={`group bg-white rounded-2xl border overflow-hidden hover:-translate-y-1 transition-all duration-300 ${
      isComingSoon ? 'border-slate-200 opacity-70' : 'border-slate-200 hover:border-[#F4A340]/50 hover:shadow-xl'
    }`}>
      <div className="relative aspect-square bg-slate-50 overflow-hidden">
        {product.image ? (
          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
            <Package className="w-16 h-16 mb-2" strokeWidth={1} />
            <span className="text-xs uppercase tracking-widest">Bientôt</span>
          </div>
        )}
        {product.featured && !isComingSoon && (
          <div className="absolute top-3 left-3 bg-[#F4A340] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1 shadow-md">
            <Sparkles className="w-3 h-3" /> Populaire
          </div>
        )}
        {isComingSoon && (
          <div className="absolute inset-0 bg-slate-100/60 backdrop-blur-[2px] flex items-center justify-center">
            <span className="px-4 py-1.5 bg-slate-900/80 text-white text-xs font-semibold rounded-full uppercase tracking-widest">À venir</span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#E89534] font-semibold mb-1">{product.category}</div>
        <h3 className="font-bold text-slate-900 leading-snug min-h-[3rem]">{product.name}</h3>
        {product.description && (
          <p className="text-xs text-slate-500 mt-2 line-clamp-2 min-h-[2.5rem]">{product.description}</p>
        )}

        <div className="mt-4 flex items-end justify-between">
          <div>
            {isComingSoon ? (
              <span className="text-slate-400 text-sm italic">Bientôt disponible</span>
            ) : (
              <>
                <div className="text-2xl font-bold text-slate-900">{formatFCFA(product.price)}</div>
                <div className="text-[10px] text-slate-500">F CFA</div>
              </>
            )}
          </div>
          <button
            onClick={handleAdd}
            disabled={isComingSoon || product.stock === 0}
            className={`flex items-center gap-1 px-4 py-2.5 rounded-full text-sm font-semibold transition-all ${
              isComingSoon || product.stock === 0
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : added
                ? 'bg-emerald-500 text-white'
                : 'bg-[#F4A340] hover:bg-[#E89534] text-white shadow-sm hover:shadow-md'
            }`}
          >
            {added ? <><Check className="w-4 h-4" /> Ajouté</> : <><Plus className="w-4 h-4" /> Panier</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const CartDrawer = ({ open, onClose, onCheckout }) => {
  const { items, updateQty, removeItem, totalAmount, clear } = useCart();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex justify-end">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="bg-gradient-to-br from-[#F4A340] to-[#E89534] px-6 py-5 text-white flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              <h2 className="text-xl font-bold">Mon panier</h2>
            </div>
            <p className="text-white/90 text-xs mt-1">{items.length} article{items.length > 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <ShoppingBag className="w-14 h-14 mx-auto mb-3 opacity-40" />
              <p>Votre panier est vide</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map((it) => (
                <li key={it.id} className="flex gap-3 p-3 rounded-2xl border border-slate-100">
                  {it.image ? (
                    <img src={it.image} alt={it.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{it.name}</div>
                    <div className="text-[#E89534] font-bold text-sm mt-1">{formatFCFA(it.price)} F</div>
                    <div className="flex items-center gap-1 mt-2">
                      <button onClick={() => updateQty(it.id, it.qty - 1)} className="w-7 h-7 rounded-lg border border-slate-200 hover:border-[#F4A340] flex items-center justify-center">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-sm font-semibold">{it.qty}</span>
                      <button onClick={() => updateQty(it.id, it.qty + 1)} className="w-7 h-7 rounded-lg border border-slate-200 hover:border-[#F4A340] flex items-center justify-center">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeItem(it.id)} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 self-start">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-slate-100 p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Sous-total</span>
              <span className="font-semibold">{formatFCFA(totalAmount)} F</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Livraison</span>
              <span className="text-slate-500">À calculer</span>
            </div>
            <div className="flex justify-between text-lg pt-3 border-t border-slate-100">
              <span className="font-bold">Total</span>
              <span className="font-bold text-[#E89534]">{formatFCFA(totalAmount)} F CFA</span>
            </div>
            <button
              onClick={() => { onClose(); onCheckout(); }}
              className="w-full py-3 bg-[#F4A340] hover:bg-[#E89534] text-white font-semibold rounded-full transition-colors"
            >
              Passer commande
            </button>
            <button onClick={clear} className="w-full text-xs text-slate-500 hover:text-red-500 py-1">
              Vider le panier
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const BoutiquePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [category, setCategory] = useState('Toutes');
  const { addItem, totalItems } = useCart();

  useEffect(() => {
    publicApi.getProducts()
      .then(setProducts)
      .finally(() => setLoading(false));
  }, []);

  const categories = ['Toutes', ...Array.from(new Set(products.map(p => p.category)))];
  const filtered = category === 'Toutes' ? products : products.filter(p => p.category === category);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-[#FAF7F2] to-orange-50/50 relative overflow-hidden">
        <div className="absolute -top-10 right-10 w-40 h-40 rotate-45 bg-[#F4A340]/10 rounded-3xl blur-2xl" />
        <div className="max-w-5xl mx-auto text-center relative">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F4A340]/10 text-[#E89534] text-xs font-semibold tracking-widest uppercase mb-4">
            Boutique en ligne
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight mb-4">
            Équipez-vous pour <span className="text-[#E89534]">l'aventure</span>
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            Sécurité, équipement et souvenirs — tout ce qu'il faut pour profiter pleinement de vos sorties sur la lagune.
          </p>
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-600">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E89534]" /> Produits certifiés</div>
            <div className="flex items-center gap-2"><Package className="w-4 h-4 text-[#E89534]" /> Livraison Cotonou</div>
          </div>
        </div>

        {/* Floating cart */}
        <button
          onClick={() => setCartOpen(true)}
          className="fixed bottom-24 right-6 z-40 flex items-center gap-2 px-5 py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full shadow-2xl hover:scale-105 transition-all"
        >
          <ShoppingBag className="w-5 h-5" />
          Panier
          {totalItems > 0 && (
            <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#F4A340] text-white text-xs font-bold flex items-center justify-center">
              {totalItems}
            </span>
          )}
        </button>
      </section>

      {/* Products */}
      <section className="py-14 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Category filters */}
          <div className="flex gap-2 flex-wrap mb-8">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === c ? 'bg-[#F4A340] text-white' : 'bg-white border border-slate-200 hover:border-[#F4A340]'
                }`}
              >{c}</button>
            ))}
          </div>

          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F4A340]" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} onAdd={(prod) => addItem(prod, 1)} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => setCheckoutOpen(true)} />
      <CheckoutModal open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </div>
  );
};

export default BoutiquePage;
